'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  ChartBarIcon,
  CubeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CogIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  StarIcon,
  CalendarDaysIcon,
  VideoCameraIcon,
  WifiIcon,
  DocumentCheckIcon,
  ComputerDesktopIcon,
  PresentationChartLineIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Icon from '@/components/ui/AppIcon';


const modules = [
  {
    icon: ChartBarIcon,
    title: 'Finance & Accounting',
    desc: 'Real-time P&L, automated invoicing, multi-currency support, and audit-ready reports.',
    span: 'md:col-span-2',
    accent: '#00C9A7',
  },
  {
    icon: CubeIcon,
    title: 'Inventory Management',
    desc: 'Live stock tracking, reorder automation, warehouse management across multiple locations.',
    span: '',
    accent: '#3B82F6',
  },
  {
    icon: UserGroupIcon,
    title: 'HR & Payroll',
    desc: 'Employee lifecycle, automated payroll, leave management, and compliance reporting.',
    span: '',
    accent: '#8B5CF6',
  },
  {
    icon: BuildingOfficeIcon,
    title: 'CRM & Sales',
    desc: 'Pipeline management, lead scoring, quote generation, and customer 360° view.',
    span: 'md:col-span-2',
    accent: '#F59E0B',
  },
  {
    icon: TruckIcon,
    title: 'Supply Chain',
    desc: 'Vendor management, purchase orders, demand forecasting, and logistics tracking.',
    span: '',
    accent: '#EF4444',
  },
  {
    icon: CogIcon,
    title: 'Manufacturing',
    desc: 'Production planning, BOM management, quality control, and shop floor tracking.',
    span: '',
    accent: '#06B6D4',
  },
];

const additionalServices = [
  {
    icon: VideoCameraIcon,
    title: 'CCTV Installation',
    desc: 'Professional CCTV setup for homes and businesses. HD cameras, remote monitoring, night vision, and 24/7 recording with cloud backup.',
    span: 'md:col-span-2',
    accent: '#EF4444',
  },
  {
    icon: WifiIcon,
    title: 'WiFi Installation',
    desc: 'Enterprise-grade wireless network design, installation, and configuration for seamless coverage across your premises.',
    span: '',
    accent: '#3B82F6',
  },
  {
    icon: DocumentCheckIcon,
    title: 'Company Registrations',
    desc: 'End-to-end company registration, CIPC filings, tax number applications, and compliance documentation handled for you.',
    span: '',
    accent: '#00C9A7',
  },
  {
    icon: ComputerDesktopIcon,
    title: 'Desktop Support',
    desc: 'On-site and remote desktop support, hardware repairs, software installations, and IT troubleshooting for your team.',
    span: '',
    accent: '#8B5CF6',
  },
  {
    icon: PresentationChartLineIcon,
    title: 'Data Analytics',
    desc: 'Transform raw business data into actionable insights. Custom dashboards, reporting pipelines, and BI solutions tailored to your industry.',
    span: 'md:col-span-2',
    accent: '#F59E0B',
  },
];

const testimonials = [
  {
    quote: 'MANGQASHA IT SOLUTIONS cut our month-end close from 12 days to 3. The finance module alone paid for itself in 4 months.',
    name: 'Priya Mehta',
    role: 'CFO, Meridian Logistics',
    metric: '75% faster close',
  },
  {
    quote: 'We manage 8 warehouses from a single dashboard. Inventory accuracy went from 82% to 99.1% in 6 weeks.',
    name: 'James Okafor',
    role: 'Operations Director, TradeBridge',
    metric: '99.1% accuracy',
  },
  {
    quote: 'The Facebook and Instagram integration drove R240K in social commerce revenue in our first quarter.',
    name: 'Sofia Reyes',
    role: 'Head of E-Commerce, Vantara Retail',
    metric: 'R240K social revenue',
  },
];

const plans = [
  {
    name: 'Starter',
    price: 'R4 825,74',
    period: '/mo',
    desc: 'For businesses with up to 50 employees',
    features: ['Finance & Accounting', 'Inventory Management', 'HR & Payroll', '5 user seats', 'Email support'],
    cta: 'Get Started',
    highlight: false,
  },
  {
    name: 'Professional',
    price: 'R11 281,47',
    period: '/mo',
    desc: 'For growing teams up to 200 employees',
    features: ['All Starter modules', 'CRM & Sales', 'Supply Chain', 'Social Selling (FB + IG)', '25 user seats', 'Priority support'],
    cta: 'Most Popular',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'For large organizations with complex needs',
    features: ['All modules included', 'Manufacturing', 'Unlimited users', 'Custom integrations', 'Dedicated CSM', 'SLA guarantee'],
    cta: 'Contact Sales',
    highlight: false,
  },
];

const productSlides = [
  {
    src: '/assets/images/ChatGPT_Image_Aug_13__2026__11_38_11_AM-1786614063190.png',
    alt: 'MANGQASHA IT SOLUTIONS Website & App Development packages',
    label: 'Website & App Development',
    desc: 'Custom digital solutions for South African businesses',
    color: '#00C9A7',
  },
  {
    src: '/assets/images/IMG_3783-1786612336585.jpg',
    alt: 'Business and Administration services — company registration packages',
    label: 'Company Registrations',
    desc: 'Professional business registration & compliance services',
    color: '#6366F1',
  },
  {
    src: '/assets/images/IMG_3870-1786612332641.jpg',
    alt: 'Store Management POS system showing point-of-sale interface',
    label: 'Store Management',
    desc: 'Inventory & retail operations in one platform',
    color: '#F97316',
  },
  {
    src: '/assets/images/5D51921B-BF56-4235-85C8-C6413BEEA139-1786611963708.PNG',
    alt: 'MANG ASSET dashboard showing asset tracking and lifecycle management',
    label: 'MANG ASSET',
    desc: 'Smart asset management & lifecycle tracking',
    color: '#00C9A7',
  },
  {
    src: '/assets/images/D7DF6F62-6F81-4606-A9F5-901D11D8A357-1786611964568.PNG',
    alt: 'ENYANDENI CLOCKING system showing employee attendance interface',
    label: 'ENYANDENI CLOCKING',
    desc: 'Workforce time & attendance management',
    color: '#3B82F6',
  },
  {
    src: '/assets/images/2272B5C0-879A-47CF-B199-F3678C521BC5-1786611964542.PNG',
    alt: 'PROP FLOW property management ERP showing tenant and lease management',
    label: 'PROP FLOW',
    desc: 'Property management simplified',
    color: '#F59E0B',
  },
];

function ProductSlideshow() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % productSlides?.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const prev = () => setCurrent((c) => (c - 1 + productSlides?.length) % productSlides?.length);
  const next = () => setCurrent((c) => (c + 1) % productSlides?.length);

  const slide = productSlides?.[current];

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8 text-center">
        <p className="section-label mb-2">Our Products</p>
        <h2 className="text-3xl font-extrabold text-white">Solutions built for your business</h2>
      </div>
      <div className="relative rounded-2xl overflow-hidden border border-[#1E3352] bg-[#162236] group" style={{ height: '420px' }}>
        {/* Slide image */}
        <Image
          key={current}
          src={slide?.src}
          alt={slide?.alt}
          fill
          className="object-cover transition-opacity duration-700"
          sizes="(max-width: 768px) 100vw, 1280px"
          priority={current === 0}
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1B2D]/90 via-[#0F1B2D]/30 to-transparent" />

        {/* Slide info */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="flex items-end justify-between">
            <div>
              <span
                className="inline-block text-xs font-bold px-3 py-1 rounded-full mb-3"
                style={{ backgroundColor: `${slide?.color}20`, color: slide?.color, border: `1px solid ${slide?.color}40` }}
              >
                {slide?.label}
              </span>
              <p className="text-white text-xl font-bold">{slide?.desc}</p>
            </div>
            <Link
              href="/products"
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-[#0F1B2D] transition-colors"
              style={{ backgroundColor: slide?.color }}
            >
              View All <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Prev / Next */}
        <button
          onClick={prev}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
          aria-label="Previous product"
        >
          <ChevronLeftIcon className="w-5 h-5 text-white" />
        </button>
        <button
          onClick={next}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
          aria-label="Next product"
        >
          <ChevronRightIcon className="w-5 h-5 text-white" />
        </button>

        {/* Dot indicators */}
        <div className="absolute top-4 right-4 flex gap-1.5">
          {productSlides?.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className="w-2 h-2 rounded-full transition-all"
              style={{ backgroundColor: i === current ? slide?.color : 'rgba(255,255,255,0.3)' }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen bg-[#0F1B2D]">
      <Navbar />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1B2D] via-[#162236] to-[#1A2D45]" />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-5">
          <div className="w-full h-full" style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, #00C9A7 0%, transparent 60%)' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            {/* Left 3/5 */}
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00C9A7]/10 border border-[#00C9A7]/20 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00C9A7] animate-pulse" />
                <span className="text-xs font-semibold text-[#00C9A7]">Enterprise ERP Software</span>
              </div>
              <h1 className="text-hero-xl text-white mb-5">
                Run Your Entire<br />
                <span className="text-[#00C9A7]">Business</span> From<br />
                One Platform
              </h1>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-8 max-w-lg">
                Finance, HR, inventory, CRM, supply chain, and manufacturing — unified in one ERP. Plus built-in social selling for Facebook and Instagram.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link href="/products" className="btn-primary px-6 py-3 text-sm inline-flex items-center gap-2">
                  Explore ERP Plans <ArrowRightIcon className="w-4 h-4" />
                </Link>
                <Link href="/book-demo" className="btn-outline px-6 py-3 text-sm inline-flex items-center gap-2">
                  <CalendarDaysIcon className="w-4 h-4" /> Book a Free Demo
                </Link>
              </div>
              <div className="flex items-center gap-6 mt-8">
                {[['500+', 'Demos Booked'], ['98%', 'Customer Retention'], ['34%', 'Avg Cost Reduction']]?.map(([val, label]) => (
                  <div key={label}>
                    <p className="text-xl font-extrabold text-white">{val}</p>
                    <p className="text-xs text-[#64748B]">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right 2/5 — Dashboard mockup */}
            <div className="lg:col-span-2">
              <div className="relative">
                <div className="bg-[#162236] border border-[#1E3352] rounded-2xl p-5 teal-glow">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#00C9A7]" />

                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {[
                      { label: 'Monthly Revenue', val: 'R2.4M', change: '+12.3%', color: '#00C9A7' },
                      { label: 'Open Orders', val: '1,847', change: '+8.1%', color: '#3B82F6' },
                      { label: 'Active Employees', val: '342', change: '+5', color: '#8B5CF6' },
                      { label: 'Inventory Value', val: 'R890K', change: '-2.1%', color: '#F59E0B' },
                    ]?.map((m) => (
                      <div key={m?.label} className="bg-[#0F1B2D] rounded-xl p-3 border border-[#1E3352]">
                        <p className="text-[10px] text-[#64748B] mb-1">{m?.label}</p>
                        <p className="text-base font-bold text-white">{m?.val}</p>
                        <p className="text-[10px] font-semibold" style={{ color: m?.color }}>{m?.change}</p>
                      </div>
                    ))}
                  </div>
                  {/* Mini chart bars */}
                  <div className="bg-[#0F1B2D] rounded-xl p-3 border border-[#1E3352]">
                    <p className="text-[10px] text-[#64748B] mb-2">Revenue — Last 6 months</p>
                    <div className="flex items-end gap-1.5 h-12">
                      {[40, 55, 45, 70, 60, 85]?.map((h, i) => (
                        <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}%`, background: i === 5 ? '#00C9A7' : '#1E3352' }} />
                      ))}
                    </div>
                  </div>
                </div>
                {/* Floating badge */}
                <div className="absolute -top-3 -right-3 bg-[#00C9A7] text-[#0F1B2D] text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                  Live Data
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Product Slideshow */}
      <ProductSlideshow />
      {/* ERP Modules Bento Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="mb-10">
          <p className="section-label mb-2">ERP Modules</p>
          <h2 className="text-3xl font-extrabold text-white">Everything your business needs,<br />built in from day one</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {modules?.map((mod) => {
            const Icon = mod?.icon;
            return (
              <div
                key={mod?.title}
                className={`${mod?.span} bg-[#162236] border border-[#1E3352] rounded-2xl p-6 card-hover`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${mod?.accent}18` }}>
                  <Icon className="w-5 h-5" style={{ color: mod?.accent }} />
                </div>
                <h3 className="text-base font-bold text-white mb-2">{mod?.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{mod?.desc}</p>
              </div>
            );
          })}
        </div>
      </section>
      {/* Social Selling Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#162236] via-[#1A2D45] to-[#162236] border border-[#1E3352] p-8 md:p-12">
          <div className="absolute right-0 top-0 w-64 h-full opacity-10" style={{ background: 'radial-gradient(circle at 80% 50%, #00C9A7, transparent 70%)' }} />
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="section-label mb-2">Social Commerce</p>
              <h2 className="text-2xl font-extrabold text-white mb-2">Sell Directly on Facebook & Instagram</h2>
              <p className="text-[#94A3B8] text-sm max-w-lg">Connect your ERP product catalog to Facebook Shop and Instagram Shopping. Sync inventory, manage orders, and track social revenue — all from MANGQASHA IT SOLUTIONS.</p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="flex items-center gap-2 bg-[#0F1B2D] border border-[#1E3352] rounded-xl px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-[#1877F2] flex items-center justify-center">
                  <span className="text-white font-black text-sm">f</span>
                </div>
                <span className="text-sm font-semibold text-white">Facebook</span>
              </div>
              <div className="flex items-center gap-2 bg-[#0F1B2D] border border-[#1E3352] rounded-xl px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center">
                  <span className="text-white font-black text-xs">IG</span>
                </div>
                <span className="text-sm font-semibold text-white">Instagram</span>
              </div>
            </div>
            <Link href="/social-selling" className="btn-primary px-5 py-3 text-sm whitespace-nowrap">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="mb-10">
          <p className="section-label mb-2">Additional Services</p>
          <h2 className="text-3xl font-extrabold text-white">More ways we support<br />your business growth</h2>
          <p className="text-[#64748B] text-sm mt-3 max-w-xl">Beyond ERP software, MANGQASHA IT SOLUTIONS delivers hands-on technology and compliance services to keep your operations running smoothly.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {additionalServices?.map((svc) => {
            const SvcIcon = svc?.icon;
            return (
              <div
                key={svc?.title}
                className={`${svc?.span} bg-[#162236] border border-[#1E3352] rounded-2xl p-6 card-hover group`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${svc?.accent}18` }}>
                    <SvcIcon className="w-5 h-5" style={{ color: svc?.accent }} />
                  </div>
                  <div className="w-2 h-2 rounded-full opacity-60" style={{ background: svc?.accent }} />
                </div>
                <h3 className="text-base font-bold text-white mb-2">{svc?.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{svc?.desc}</p>
                <div className="mt-4 pt-4 border-t border-[#1E3352]">
                  <Link href="/book-demo" className="inline-flex items-center gap-1.5 text-xs font-semibold transition-colors" style={{ color: svc?.accent }}>
                    Get a Quote <ArrowRightIcon className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <p className="section-label mb-2">Pricing</p>
          <h2 className="text-3xl font-extrabold text-white mb-3">Transparent pricing, no surprises</h2>
          <p className="text-[#64748B] text-sm">All plans include a 30-day free trial. No credit card required.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans?.map((plan) => (
            <div
              key={plan?.name}
              className={`rounded-2xl p-6 border ${
                plan?.highlight
                  ? 'bg-[#00C9A7]/5 border-[#00C9A7]/40 relative'
                  : 'bg-[#162236] border-[#1E3352]'
              }`}
            >
              {plan?.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00C9A7] text-[#0F1B2D] text-xs font-bold px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              <h3 className="text-lg font-bold text-white mb-1">{plan?.name}</h3>
              <p className="text-xs text-[#64748B] mb-4">{plan?.desc}</p>
              <div className="flex items-baseline gap-1 mb-5">
                <span className="text-3xl font-extrabold text-white">{plan?.price}</span>
                <span className="text-sm text-[#64748B]">{plan?.period}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {plan?.features?.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-[#94A3B8]">
                    <CheckCircleIcon className="w-4 h-4 text-[#00C9A7] shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col gap-2">
                <Link
                  href="/book-demo"
                  className={`text-center py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    plan?.highlight
                      ? 'bg-[#00C9A7] text-[#0F1B2D] hover:bg-[#00B896]'
                      : 'border border-[#1E3352] text-[#94A3B8] hover:border-[#00C9A7] hover:text-white'
                  }`}
                >
                  Book Demo
                </Link>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-6">
          <Link href="/products" className="text-sm text-[#00C9A7] hover:underline inline-flex items-center gap-1">
            See full feature comparison <ArrowRightIcon className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>
      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-10">
          <p className="section-label mb-2">Customer Stories</p>
          <h2 className="text-3xl font-extrabold text-white">Real results from real businesses</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials?.map((t) => (
            <div key={t?.name} className="bg-[#162236] border border-[#1E3352] rounded-2xl p-6 card-hover">
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)]?.map((_, i) => (
                  <StarIcon key={i} className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B]" />
                ))}
              </div>
              <p className="text-sm text-[#94A3B8] leading-relaxed mb-5 italic">"{t?.quote}"</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">{t?.name}</p>
                  <p className="text-xs text-[#64748B]">{t?.role}</p>
                </div>
                <div className="bg-[#00C9A7]/10 border border-[#00C9A7]/20 rounded-lg px-3 py-1.5">
                  <p className="text-xs font-bold text-[#00C9A7]">{t?.metric}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      {/* Demo CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gradient-to-r from-[#00C9A7]/10 via-[#162236] to-[#00C9A7]/5 border border-[#00C9A7]/20 rounded-3xl p-10 md:p-16 text-center teal-glow">
          <CalendarDaysIcon className="w-12 h-12 text-[#00C9A7] mx-auto mb-4" />
          <h2 className="text-3xl font-extrabold text-white mb-3">See MANGQASHA IT SOLUTIONS in Action</h2>
          <p className="text-[#94A3B8] text-base mb-8 max-w-md mx-auto">
            Get a personalized 45-minute demo tailored to your industry and business size. No sales pressure — just answers.
          </p>
          <Link href="/book-demo" className="btn-primary px-8 py-3.5 text-base inline-flex items-center gap-2">
            <CalendarDaysIcon className="w-5 h-5" /> Book Your Free Demo
          </Link>
          <p className="text-xs text-[#64748B] mt-4">500+ demos completed · 4.9/5 average rating</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}